import { Drawer, DrawerBody, DrawerContent, DrawerHeader } from '@heroui/react';
import { ELightGalleryType } from '@quesmed/types/models';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { BsImage, BsUpload } from 'react-icons/bs';
import { assetFieldMapping, EFormMode, type IAssetSchema } from '@/config';
import { AssetCard } from '@/core/molecules';
import { Button, cn } from '@/core/ui';
import { useAsset } from '@/store';
import { useRecord } from '@/store/record';
import { useAssetActions } from './use-asset-actions';

type ISchema = { assets: IAssetSchema[]; [x: string]: unknown };

export const AssetModal = ({ type }: { type: 'job' | 'normal' }) => {
  const setAssetEditor = useAsset.use.setAssetEditor();
  const assetModal = useAsset.use.assetModal();
  const closeAssetModal = useAsset.use.closeAssetModal();
  const formMode = useRecord.use.formMode();
  const currentRecord = useRecord.use.currentRecord();
  const setAssetMapping = useAsset.use.setAssetMapping();
  const { setValue, getValues, watch } = useFormContext<ISchema>();
  const currentAssets = watch('assets') || [];
  const { removeJobAsset, removeAsset, removeAssetsLoading } = useAssetActions();
  const formActions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };

  const onRemoveJobAsset = async (asset: IAssetSchema, field: keyof ISchema) => {
    const imageText = `[lightgallery${asset.index ? asset.index : ''}]`;

    if (asset.recordId) {
      //  asset is linked to record, so just remove from form
      const newAssets = currentAssets.map((a) => (a.id === asset.id ? { ...a, deleted: true } : a));
      setValue('assets', newAssets, formActions);
      setValue(
        field as string,
        ((getValues(field as string) as string) ?? '')?.replace(imageText, ''),
        formActions,
      );
      setAssetMapping(newAssets.filter((a) => !a.deleted));
    } else {
      //  asset is not linked to record, so remove from database
      removeJobAsset(asset).then(async ({ data }) => {
        const newAssets = currentAssets.filter((a) => a.id !== asset.id);
        if (data?.admin?.removeJobAssets) {
          setValue('assets', newAssets, formActions);
          setValue(
            field as string,
            ((getValues(field as string) as string) ?? '')?.replace(imageText, ''),
            formActions,
          );
          setAssetMapping(newAssets);
        }
      });
    }
  };

  const onRemoveAsset = async (asset: IAssetSchema, field: keyof ISchema) => {
    const imageText = `[lightgallery${asset.index ? asset.index : ''}]`;

    if (asset.linked) {
      //  asset is linked to record, so just remove from form
      const newAssets = currentAssets.map((a) => (a.id === asset.id ? { ...a, deleted: true } : a));
      setValue('assets', newAssets, formActions);
      setValue(field as string, (getValues(field as string) as string)?.replace(imageText, ''), formActions);
      setAssetMapping(newAssets.filter((a) => !a.deleted));
    } else {
      //  asset is not linked to record, so remove from database
      removeAsset(asset).then(async ({ data }) => {
        const newAssets = currentAssets.filter((a) => a.id !== asset.id);
        if (data?.admin?.removeAssets) {
          setValue('assets', newAssets, formActions);
          setValue(
            field as string,
            (getValues(field as string) as string)?.replace(imageText, ''),
            formActions,
          );
          setAssetMapping(newAssets);
        }
      });
    }
  };

  const onRemove = async (asset: IAssetSchema) => {
    if (!asset) return;

    const column = assetFieldMapping[asset.type];
    let field: keyof ISchema;
    if (asset.choiceLabel != null) {
      const fieldNameMap: Partial<Record<ELightGalleryType, string>> = {
        [ELightGalleryType.CHOICE]: 'name',
        [ELightGalleryType.CHOICE_EXPLANATION]: 'explanation',
      };
      const fieldName = fieldNameMap[asset.type] ?? 'name';
      const choiceIdx = asset.choiceLabel.toLowerCase().charCodeAt(0) - 97;
      field = `${column}.${choiceIdx}.${fieldName}` as keyof ISchema;
    } else {
      field = column as keyof ISchema;
    }
    if (type === 'job') {
      await onRemoveJobAsset(asset, field);
    } else {
      await onRemoveAsset(asset, field);
    }
  };

  // const _onRemoveAll = () => {
  //   if (!currentRecord?.assets) return;
  //   const { jobId, assets } = currentRecord;
  //   removeJobAssets({
  //     variables: { jobId: jobId, ids: assets.map((a) => a.id), unlink: true },
  //   }).then(({ data }) => {
  //     if (data?.admin?.removeJobAssets) {
  //       setValue('assets', data.admin.removeJobAssets);
  //       setAssetMapping(data.admin.removeJobAssets);
  //     }
  //   });
  // };

  const assets = useMemo(() => {
    if (!assetModal) {
      return [];
    }

    const { key, choiceLabel } = assetModal;
    return currentAssets
      .filter((asset) => {
        // filter out deleted assets
        if (asset.deleted) return false;
        // filter out assets that don't match the key and choice label
        if (choiceLabel != null) {
          return asset.type === key && asset.choiceLabel === choiceLabel;
        }
        // filter out assets that don't match the key
        if (key) {
          return asset.type === key;
        }
        return true;
      })
      .sort((a, b) => {
        const aId = a.picture?.id ? Number(a.picture.id) : 0;
        const bId = b.picture?.id ? Number(b.picture?.id) : 0;

        return aId - bId; // ascending by picture id
      });
  }, [assetModal, currentAssets]);

  const createMode = formMode === EFormMode.CREATE;

  return (
    <Drawer
      isDismissable={false}
      size='xl'
      radius='none'
      // radius='sm'
      // classNames={{
      //   body: 'min-h-[70vh]',
      // }}
      isOpen={assetModal.isOpen}
      onClose={closeAssetModal}
    >
      <DrawerContent>
        <DrawerHeader className='border-b border-default-200'>
          {createMode ? 'Create' : 'Update'} {assetModal.title} Assets
        </DrawerHeader>
        <DrawerBody
          className={cn('py-4', {
            'opacity-50 pointer-events-none': removeAssetsLoading,
          })}
        >
          {/* <div className='bg-content1 border border-dashed border-default-200 rounded-lg p-4'>
            <AssetUpload onSubmit={onSubmit} />
          </div> */}
          {assets && assets.length > 0 && (
            <div className='flex justify-between items-center mb-4'>
              <span>Assets ({assets?.length})</span>

              <div className='flex gap-2'>
                {assetModal.key && (
                  <Button
                    variant='bordered'
                    size='sm'
                    startContent={<BsUpload />}
                    onPress={() =>
                      setAssetEditor({
                        key: assetModal.key ?? ELightGalleryType.QUESTION,
                        choiceLabel: assetModal.choiceLabel,
                      })
                    }
                  >
                    Add Asset
                  </Button>
                )}
                {/* <Button
                  isLoading={loading}
                  isDisabled={loading}
                  variant='bordered'
                  size='sm'
                  startContent={<BsTrash />}
                  onPress={onRemoveAll}
                >
                  Remove All
                </Button> */}
              </div>
            </div>
          )}
          {assets && assets.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {assets.map((asset) => (
                <AssetCard
                  linked={asset.linked ?? false}
                  currentRecordId={currentRecord?.id}
                  asset={asset}
                  key={asset.id}
                  onRemove={onRemove}
                />
              ))}
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center py-16 text-default-400'>
              <BsImage className='size-24 mb-4 text-default-300' />
              <div className='text-lg font-medium mb-1'>No assets found</div>
              <div className='text-sm text-default-400 mb-8'>You haven't added any assets yet.</div>
              {/* <Button isDisabled={loading} variant='flat' color='success' startContent={<BsUpload />}>
                Add Assets
              </Button> */}
            </div>
          )}
        </DrawerBody>
        {/* <ModalFooter className='border-t border-default-200 flex justify-between items-center'>
          <span className='text-default-500'>
            {createMode ? 'You can upload up to 10 assets' : 'You can upload up to 10 assets'}
          </span>
          <div className='flex gap-2'>
            <Button onPress={() => setAssetModal(false)}>Close</Button>
            <Button color='success' onPress={() => setAssetModal(false)}>
              Save
            </Button>
          </div>
        </ModalFooter> */}
      </DrawerContent>
    </Drawer>
  );
};
